import { TrashIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";

interface RosterEditDrawerFooterProps {
  canRemove: boolean;
  onDismiss?: () => void;
  saving: boolean;
  dismissing: boolean;
  onCancel: () => void;
  onRemove: () => void;
}

export const RosterEditDrawerFooter = ({
  canRemove,
  onDismiss,
  saving,
  dismissing,
  onCancel,
  onRemove,
}: RosterEditDrawerFooterProps) => {
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const savingEdits = saving && !dismissing;

  return (
    <Stack
      gap="sm"
      fullWidth
      className="border-t border-stroke-tertiary px-3 py-4"
    >
      {confirmingRemove ? (
        <Stack
          direction="row"
          align="center"
          justify="between"
          gap="sm"
          fullWidth
          className="flex-wrap"
        >
          <Typography variant="bodyW3" textColor="secondary">
            Remove this person from the roster?
          </Typography>
          <Stack direction="row" align="center" gap="sm">
            <Button
              type="button"
              size="xs"
              variant="secondary"
              disabled={saving}
              onClick={() => setConfirmingRemove(false)}
            >
              Keep
            </Button>
            <Button
              type="button"
              size="xs"
              variant="destructive"
              loading={saving}
              disabled={saving}
              onClick={onRemove}
            >
              Remove
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Stack
          direction="row"
          align="center"
          justify={canRemove || onDismiss ? "between" : "end"}
          gap="sm"
          fullWidth
          className="flex-nowrap"
        >
          {canRemove && (
            <Button
              type="button"
              size="xs"
              variant="text"
              leftIcon={<TrashIcon />}
              className="shrink-0 text-negative-text hover:text-negative-text"
              disabled={saving}
              onClick={() => setConfirmingRemove(true)}
            >
              Remove from roster
            </Button>
          )}
          {onDismiss && (
            <Button
              type="button"
              size="xs"
              variant="text"
              disabled={saving}
              loading={dismissing}
              onClick={onDismiss}
            >
              Not on payroll
            </Button>
          )}
          <Stack direction="row" align="center" gap="sm" className="shrink-0">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={saving}
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              loading={savingEdits}
              disabled={saving}
            >
              {savingEdits ? "Saving roster..." : "Save changes"}
            </Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
